import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  Search,
  MoreVertical,
  UserCheck,
  UserX,
  Loader2,
  Mail
} from "lucide-react";
import { Input } from "../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/toggle-status`);
      setUsers(users.map(u => 
        u.id === userId ? { ...u, is_active: response.data.is_active } : u
      ));
      toast.success(response.data.is_active ? "Utilisateur activé" : "Utilisateur désactivé");
    } catch (error) {
      console.error("Error toggling user status:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Utilisateurs
          </h1>
          <p className="text-slate-500 mt-1">
            {users.length} utilisateurs enregistrés
          </p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 rounded-xl"
            data-testid="search-users"
          />
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 p-4 bg-slate-50 border-b border-slate-100 text-sm font-medium text-slate-500">
          <div className="col-span-4">Utilisateur</div>
          <div className="col-span-3 hidden md:block">Email</div>
          <div className="col-span-2 hidden md:block">Inscription</div>
          <div className="col-span-2">Statut</div>
          <div className="col-span-1"></div>
        </div>

        {/* Table Body */}
        {filteredUsers.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {filteredUsers.map((user) => (
              <div 
                key={user.id} 
                className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-slate-50 transition-colors"
                data-testid={`user-row-${user.id}`}
              >
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                    <span className="font-medium text-slate-600">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-sm text-slate-500 truncate md:hidden">
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="col-span-3 hidden md:block">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="truncate">{user.email}</span>
                  </div>
                </div>
                <div className="col-span-2 hidden md:block text-sm text-slate-500">
                  {formatDate(user.created_at)}
                </div>
                <div className="col-span-2">
                  {user.is_active !== false ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                      <UserCheck className="w-3 h-3" />
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                      <UserX className="w-3 h-3" />
                      Inactif
                    </span>
                  )}
                  {user.is_admin && (
                    <span className="inline-flex items-center ml-2 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                      Admin
                    </span>
                  )}
                </div>
                <div className="col-span-1 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleToggleStatus(user.id)}>
                        {user.is_active !== false ? (
                          <>
                            <UserX className="w-4 h-4 mr-2" />
                            Désactiver
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4 mr-2" />
                            Activer
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun utilisateur trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
